// Functions for reading and writing Firebase user accounts and user profiles, which are saved in Firestore.

import firebase from "./firebase";
import { firestore as db } from "./firebase";
import parse from "date-fns/parse";
import { dateFormat } from "../constants";
import { BaseModel } from "./models";
import { format } from "date-fns";

// The names of the public and private userprofiles Firestore collections.
const COLLECTION_PUBLIC_USERPROFILES: string = "userprofiles_public";
const COLLECTION_PRIVATE_USERPROFILES: string = "userprofiles_private";

// The SignUpData interface defines which data is entered in the Sign Up form when registering as a new user for the app.
export interface SignUpData {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  continent: string;
  country: string;
  region: string | null;
  municipality: string | null;
  birthdate: string | null;
  groupaccount: boolean;
  groupname: string | null;
  parentsPermission: boolean;
  agreeterms: boolean;
  currentPassword: string | null;
  newPassword: string | null;
}

// The ChangePasswordData interface defines which data is entered when changing a password.
export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  email: string;
}

// The DeleteUserData interface defines which data is entered when deleting an account.
export interface DeleteUserData {
  profileId: string;
  email: string;
  password: string;
}

// The AuthenticatedUser interface defines the properties for a user that is authenticated within the app.
export interface AuthenticatedUser {
  id: string;
  email: string;
  isAdmin: boolean;
  displayname: string;
  groupAccount: boolean;
  avatar: string;
}

// The PublicUserProfile type defines how the public data of user profiles are stored in the Firestore database.
export type PublicUserProfile = BaseModel & {
  id: string | null;
  displayname: string;
  avatar: string;
  continent: string;
  country: string;
  groupaccount: boolean;
  groupname: string | null;
  parentsPermission: boolean | undefined;
};

// The PrivateUserProfile type defines how the private data of user profiles are stored in the Firestore database.
export type PrivateUserProfile = PublicUserProfile & {
  firstname: string;
  lastname: string;
  email: string;
  region: string | null;
  municipality: string | null;
  birthdate: firebase.firestore.Timestamp;
};

// The UserError interface defines which details about authentication related errors are available.
export interface UserError {
  code: string;
  message: string;
}

function userError(message: string, code: string = "unknown"): UserError {
  return { message, code };
}

// Creates a user account in Firebase Authentication and a UserProfile in the Firestore database.
export async function createUserAccountAndProfile(signUpData: SignUpData) {
  try {
    // Create the user account for Firebase Authentication.
    const createdAccount = await firebase
      .auth()
      .createUserWithEmailAndPassword(signUpData.email, signUpData.password);

    if (!createdAccount || !createdAccount.user)
      throw new Error("Creating user account failed");

    // Create the public UserProfile in Firestore.
    const batch = db.batch();

    const publicUserProfileRef = db
      .collection(COLLECTION_PUBLIC_USERPROFILES)
      .doc(createdAccount.user.uid);
    const publicUserProfile: PublicUserProfile = toPublicUserProfileObject(
      createdAccount.user.uid,
      signUpData
    );
    batch.set(publicUserProfileRef, publicUserProfile);

    // Create the private UserProfile in Firestore.
    const privateUserProfileRef = db
      .collection(COLLECTION_PRIVATE_USERPROFILES)
      .doc(createdAccount.user.uid);
    const privateUserProfile: PrivateUserProfile = toPrivateUserProfileObject(
      createdAccount.user.uid,
      signUpData
    );
    batch.set(privateUserProfileRef, privateUserProfile);

    await batch.commit();

    // Send a verification mail to the new user.
    await firebase.auth().currentUser?.sendEmailVerification();

    log(signUpData.email + " registered successfully");
  } catch (error) {
    console.log(error.message + " - " + error.stack);
    log(
      "Registering " +
        signUpData.email +
        " failed: " +
        error.message +
        " - " +
        error.stack
    );
    throw userError(error.message, error.code);
  }
}

export type Log = BaseModel & {
  message: string;
};

async function log(message: string) {
  const document = db.collection("logs").doc();

  const logDocument: Log = {
    id: document.id,
    timestamp: firebase.firestore.Timestamp.now(),
    message,
  };

  document.set(logDocument);
}

// Update a UserProfile in the database.
export async function updateUserProfile(
  userId: string,
  signUpData: SignUpData
) {
  const existingPrivateUserProfile = await getPrivateUserProfile(userId);
  if (!existingPrivateUserProfile)
    throw Error(
      "Updating failed because the user profile does not exist anymore"
    );

  signUpData.email = existingPrivateUserProfile.email;

  // Create a PublicUserProfile and PrivateUserProfile objects based on the SignUpData.
  const publicUserProfile: PublicUserProfile = toPublicUserProfileObject(
    userId,
    signUpData
  );
  // Quick dirty fix when groupname is missing
  if (publicUserProfile.groupname == null) {
    publicUserProfile.groupaccount = false;
  }

  const privateUserProfile: PrivateUserProfile = toPrivateUserProfileObject(
    userId,
    signUpData
  );

  // Quick dirty fix when groupname is missing
  if (privateUserProfile.groupname == null) {
    privateUserProfile.groupaccount = false;
  }

  const batch = db.batch();

  // Update the PublicUserProfile in the database.
  const publicUserProfileRef = db
    .collection(COLLECTION_PUBLIC_USERPROFILES)
    .doc(userId);
  batch.update(publicUserProfileRef, publicUserProfile);

  // Update the PrivateUserProfile in the database.
  const privateUserProfileRef = db
    .collection(COLLECTION_PRIVATE_USERPROFILES)
    .doc(userId);
  batch.update(privateUserProfileRef, privateUserProfile);

  await batch.commit();
}

// Updates the password for Firebase Authentication by re-authenticating with the current password first.
export async function updatePassword(
  changePasswordData: ChangePasswordData
): Promise<boolean> {
  try {
    // To adhere to Firebase's security restrictions, a recent sign in is required to allow updating a password.
    // Therefore, re-authenticate first to ensure a recent sign in.
    await reAuthenticate(
      changePasswordData.email,
      changePasswordData.currentPassword
    );

    var user = firebase.auth().currentUser;
    if (!user) return false;
    await user.updatePassword(changePasswordData.newPassword);
    return true;
  } catch (error) {
    return false;
  }
}

// Re-authenticates the given email and password. If the credentials are invalid an error is thrown.
const reAuthenticate = async (
  email: string,
  password: string
): Promise<void> => {
  var user = firebase.auth().currentUser;
  var cred = firebase.auth.EmailAuthProvider.credential(email, password);
  const result = await user?.reauthenticateWithCredential(cred);
  if (!result) throw new Error("Re-authentication failed");
};

// Deletes the user account in Firebase Authentication and UserProfile in the Firestore database.
export async function deleteUserProfile(
  deleteUserData: DeleteUserData
): Promise<boolean> {
  try {
    // To adhere to Firebase's security restrictions, a recent sign in is required to allow deleting an account.
    // Therefore, re-authenticate first to ensure a recent sign in.
    await reAuthenticate(deleteUserData.email, deleteUserData.password);

    const batch = db.batch();

    // Delete private profile
    const privateUserProfileRef = db
      .collection(COLLECTION_PRIVATE_USERPROFILES)
      .doc(deleteUserData.profileId);
    batch.delete(privateUserProfileRef);

    // Delete public profile
    const publicUserProfileRef = db
      .collection(COLLECTION_PUBLIC_USERPROFILES)
      .doc(deleteUserData.profileId);
    batch.delete(publicUserProfileRef);

    await batch.commit();

    // Delete account
    await firebase.auth().currentUser?.delete();

    return true;
  } catch (error) {
    return false;
  }
}

export async function getPrivateUserProfile(
  userId: string
): Promise<PrivateUserProfile | null> {
  try {
    const doc = await db
      .collection(COLLECTION_PRIVATE_USERPROFILES)
      .doc(userId)
      .get();
    const userProfile = doc.data() as PrivateUserProfile;
    return userProfile;
  } catch (error) {
    return null;
  }
}

export async function getPublicUserProfile(
  userId: string
): Promise<PublicUserProfile | null> {
  try {
    const doc = await db
      .collection(COLLECTION_PUBLIC_USERPROFILES)
      .doc(userId)
      .get();
    const userProfile = doc.data() as PublicUserProfile;
    return userProfile;
  } catch (error) {
    return null;
  }
}

// Gets a PublicUserProfile snapshot from the database.
export function getPublicUserProfileSnapshot(
  userId: string,
  setUserProfile: (userProfile: PublicUserProfile | null) => void
): () => void {
  try {
    return db
      .collection(COLLECTION_PUBLIC_USERPROFILES)
      .doc(userId)
      .onSnapshot(function (doc) {
        const userProfile = doc.data() as any;
        setUserProfile(userProfile ? userProfile : null);
      });
  } catch (error) {
    setUserProfile(null);
    return () => {};
  }
}

// Gets most active users
export async function getMostActiveUsers(): Promise<PublicUserProfile[]> {
  const data = await db
    .collection(COLLECTION_PUBLIC_USERPROFILES)
    // .orderBy("callToActionCount", "desc")
    // .orderBy("commentCount", "desc")
    // .orderBy("followingCount", "desc")
    // .orderBy("givenCommentLikesCount", "desc")
    // .orderBy("proposalCount", "desc")
    // .orderBy("receivedCommentLikesCount", "desc")
    .orderBy("votes.totalCount", "desc")
    .limit(4)
    .get();

  let users: PublicUserProfile[] = [];
  data.forEach((doc) => users.push({ ...doc.data() } as any));

  return users;
}

// Creates a PublicUserProfile object based on SignUpData.
function toPublicUserProfileObject(
  userId: string,
  signUpData: SignUpData
): PublicUserProfile {
  return {
    id: userId,
    displayname: signUpData.firstname + " " + signUpData.lastname,
    avatar: signUpData.firstname.substring(0, 1).toUpperCase(),
    continent: signUpData.continent,
    country: signUpData.country,
    groupaccount: signUpData.groupaccount,
    groupname: signUpData.groupname ? signUpData.groupname : null,
    timestamp: firebase.firestore.Timestamp.now(),
    parentsPermission: signUpData.parentsPermission
      ? signUpData.parentsPermission
      : false,
  };
}

// Creates a PrivateUserProfile object based on SignUpData.
function toPrivateUserProfileObject(
  userId: string,
  signUpData: SignUpData
): PrivateUserProfile {
  const publicUserProfile: PublicUserProfile = toPublicUserProfileObject(
    userId,
    signUpData
  );

  if (!signUpData.birthdate) throw Error("Birth date shouldn't be empty");

  const privateUserProfile: PrivateUserProfile = {
    ...publicUserProfile,
    firstname: signUpData.firstname,
    lastname: signUpData.lastname,
    email: signUpData.email,
    region: signUpData.region,
    municipality: signUpData.municipality,
    birthdate: firebase.firestore.Timestamp.fromDate(
      parse(signUpData.birthdate, dateFormat, new Date())
    ),
    timestamp: firebase.firestore.Timestamp.now(),
  };

  return privateUserProfile;
}

// Creates a SignUpData object based on a PrivateUserProfile.
export function toSignUpDataObject(
  privateUserProfile: PrivateUserProfile
): SignUpData {
  return {
    firstname: privateUserProfile.firstname,
    lastname: privateUserProfile.lastname,
    email: "",
    password: "",
    continent: privateUserProfile.continent,
    country: privateUserProfile.country,
    region: privateUserProfile.region,
    municipality: privateUserProfile.municipality,
    birthdate: format(privateUserProfile.birthdate.toDate(), dateFormat),
    groupaccount: privateUserProfile.groupaccount,
    groupname: privateUserProfile.groupname,
    parentsPermission: privateUserProfile.parentsPermission
      ? privateUserProfile.parentsPermission
      : false,
    agreeterms: true,
    currentPassword: "",
    newPassword: "",
  };
}

export async function sendPasswordResetEmail(email: string) {
  try {
    await firebase.auth().sendPasswordResetEmail(email);
    return true;
  } catch (error) {
    return false;
  }
}
